import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, IconButton, MenuItem, Stack, Switch, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import { useTranslation } from 'react-i18next';
import SidebarLayout from '../components/sidebarLayout';
import { usePermissions } from '../contexts/PermissionsContext';
import { getSpaCommunicationTemplates, saveSpaCommunicationTemplate } from '../APIs/SpaAPI';
import { COMMUNICATION_CHANNELS, EMAIL_RELEASE_EVENTS, COMMUNICATION_LANGUAGES, COMMUNICATION_PLACEHOLDER_GROUPS,
  communicationPlaceholderGroup, previewTemplate, templatePayload, validTemplate } from '../utils/spaCommunications';
import useSpaCommunicationCapabilities from '../hooks/useSpaCommunicationCapabilities';
import SpaCommunicationChannels from '../components/SpaCommunicationChannels';
import SpaCommunicationRules from '../components/SpaCommunicationRules';
import SpaCommunicationHistory from '../components/SpaCommunicationHistory';

const emptyDraft = { subject: '', body: '', active: false };
const toDraft = (item) => item ? { subject: item.subject || '', body: item.body, active: item.active } : { ...emptyDraft };

function TemplatesWorkspace({ token, tenant, manage, capabilities }) {
  const { t } = useTranslation();
  const ct = (key, options) => t(`spa.communications.${key}`, options);
  const [selection, setSelection] = useState({ event: 'BOOKING_CONFIRMATION', channel: 'EMAIL', language: 'IT' });
  const [draft, setDraft] = useState(emptyDraft);
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [reload, setReload] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);
  const mounted = useRef(false);
  const saveLock = useRef(false);
  const bodyInput = useRef(null);
  const scope = JSON.stringify(selection);
  const ready = !loading && loaded?.scope === scope;
  const dirty = ready && JSON.stringify(draft) !== JSON.stringify(loaded.draft);
  const payload = templatePayload(selection, draft);
  const valid = validTemplate(payload);
  const editable = manage && ready && !saving;
  const placeholders = capabilities?.placeholders?.[communicationPlaceholderGroup(selection.event)]
    || COMMUNICATION_PLACEHOLDER_GROUPS[communicationPlaceholderGroup(selection.event)];

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setSuccess(false); setLoaded(null);
    const load = async () => {
      try {
        const filters = JSON.parse(scope);
        const data = await getSpaCommunicationTemplates(filters, token, tenant, { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (!Array.isArray(data) || data.some((item) => !validTemplate(item))) throw new Error(ct('invalidResponse'));
        const matches = data.filter((item) => item.event === filters.event && item.channel === filters.channel && item.language === filters.language);
        if (matches.length > 1) throw new Error(ct('invalidResponse'));
        const values = toDraft(matches[0]);
        setDraft(values); setLoaded({ scope, draft: values, exists: Boolean(matches[0]) });
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure.message || ct('templatesLoadError'));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
    // Language changes translate the interface without discarding the template draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, token, tenant, reload]);

  useEffect(() => {
    if (!dirty && !saving) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saving]);

  const runAction = (action) => {
    if (saveLock.current) return;
    if (action.selection) { setLoading(true); setSelection(action.selection); }
    else if (action.reset) { setDraft(loaded.draft); setError(''); setSuccess(false); }
    else { setLoading(true); setReload((value) => value + 1); }
  };
  const requestAction = (action) => dirty ? setPendingAction(action) : runAction(action);
  const change = (key, value) => { setSuccess(false); setDraft((previous) => ({ ...previous, [key]: value })); };
  const insertPlaceholder = (key) => {
    const input = bodyInput.current;
    const start = input?.selectionStart ?? draft.body.length;
    const end = input?.selectionEnd ?? start;
    change('body', `${draft.body.slice(0, start)}{{${key}}}${draft.body.slice(end)}`);
    input?.focus();
  };
  const save = async (event) => {
    event.preventDefault();
    if (!editable || !dirty || !valid || selection.channel !== 'EMAIL' || saveLock.current) return;
    saveLock.current = true; setSaving(true); setError(''); setSuccess(false);
    try {
      const data = await saveSpaCommunicationTemplate(payload, token, tenant);
      if (!mounted.current) return;
      if (!validTemplate(data) || data.event !== selection.event || data.channel !== selection.channel
        || data.language !== selection.language) throw new Error(ct('invalidResponse'));
      const values = toDraft(data);
      setDraft(values); setLoaded({ scope, draft: values, exists: true }); setSuccess(true);
    } catch (failure) {
      if (mounted.current) setError(failure.message || ct('templatesSaveError'));
    } finally {
      saveLock.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  const sample = { clientName: 'Alex', clientSurname: 'Rossi', treatmentName: ct('sampleTreatment'),
    startAt: selection.language === 'IT' ? '20 ottobre 2026, 10:00' : '20 October 2026, 10:00',
    endAt: selection.language === 'IT' ? '20 ottobre 2026, 10:30' : '20 October 2026, 10:30',
    offerExpiresAt: '20/10/2026 09:45', voucherCode: 'SAMPLE-001', voucherBalance: '75.00',
    voucherExpiresAt: '31/12/2026', packageName: ct('samplePackage'), packageExpiresAt: '31/12/2026', remainingPackageCredits: '1' };

  return <Stack spacing={3}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
      <Typography component="h2" variant="h6" fontWeight={800}>{ct('templatesTab')}</Typography>
      <Tooltip title={ct('refresh')}><span><IconButton aria-label={ct('refresh')} disabled={saving}
        onClick={() => requestAction({ refresh: true })}><RefreshIcon /></IconButton></span></Tooltip>
    </Stack>
    <Grid container spacing={2} sx={{ width: 'auto' }}>
      {['event', 'channel', 'language'].map((field) => <Grid item xs={12} sm={field === 'event' ? 6 : 3} key={field}>
        <TextField select fullWidth label={ct(field === 'language' ? 'templateLanguage' : field)} value={selection[field]} disabled={saving}
          onChange={(event) => requestAction({ selection: { ...selection, [field]: event.target.value } })}>
          {(field === 'event' ? EMAIL_RELEASE_EVENTS : field === 'channel' ? COMMUNICATION_CHANNELS : COMMUNICATION_LANGUAGES)
            .map((value) => <MenuItem key={value} value={value} disabled={field === 'channel' && value !== 'EMAIL'}>{ct(`${field}s.${value}`)}</MenuItem>)}
        </TextField>
      </Grid>)}
    </Grid>
    {capabilities?.channels?.[selection.channel]?.ready === false && <Alert severity="warning">{ct('channelNotReady')}</Alert>}
    {!capabilities && <Alert severity="info">{ct('consentNotice')}</Alert>}
    {loading && <Box role="status"><CircularProgress size={22} aria-label={ct('templatesLoading')} /></Box>}
    {error && <Alert severity="error" action={!ready && <Button color="inherit" onClick={() => requestAction({ refresh: true })}>{ct('retry')}</Button>}>{error}</Alert>}
    {success && <Alert severity="success">{ct('templateSaved')}</Alert>}
    {ready && <Grid container spacing={3} sx={{ width: 'auto' }}>
      <Grid item xs={12} md={7}>
        <Box component="form" onSubmit={save}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1} alignItems="center">
              <Typography component="h2" variant="h6">{ct(loaded.exists ? 'editTemplate' : 'newTemplate')}</Typography>
              <FormControlLabel label={ct('activeTemplate')} control={<Switch checked={draft.active} disabled={!editable}
                onChange={(event) => change('active', event.target.checked)} />} />
            </Stack>
            {selection.channel === 'EMAIL' && <TextField fullWidth required label={ct('subject')} value={draft.subject}
              disabled={!editable} inputProps={{ maxLength: 300 }} onChange={(event) => change('subject', event.target.value)} />}
            <TextField fullWidth required multiline minRows={8} label={ct('body')} value={draft.body} inputRef={bodyInput}
              disabled={!editable} inputProps={{ maxLength: 20000 }} onChange={(event) => change('body', event.target.value)} />
            <Box><Typography variant="subtitle2" sx={{ mb: 1 }}>{ct('placeholders')}</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {placeholders.map((key) => <Button key={key} size="small" disabled={!editable}
                  sx={{ textTransform: 'none', fontFamily: 'monospace' }}
                  onClick={() => insertPlaceholder(key)}>{`{{${key}}}`}</Button>)}
              </Stack>
            </Box>
            {manage ? <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title={ct('discard')}><span><IconButton aria-label={ct('discard')} disabled={!dirty || saving}
                onClick={() => requestAction({ reset: true })}><UndoIcon /></IconButton></span></Tooltip>
              <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={!editable || !dirty || !valid}>{ct('saveTemplate')}</Button>
            </Stack> : <Typography color="text.secondary">{ct('templatesReadOnly')}</Typography>}
          </Stack>
        </Box>
      </Grid>
      <Grid item xs={12} md={5}>
        <Box component="section" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, minWidth: 0 }}>
          <Typography component="h2" variant="h6" sx={{ mb: 2 }}>{ct('preview')}</Typography>
          {selection.channel === 'EMAIL' && <Typography fontWeight={700} sx={{ mb: 2, overflowWrap: 'anywhere' }}>
            {previewTemplate(draft.subject, sample)}</Typography>}
          <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{previewTemplate(draft.body, sample)}</Typography>
        </Box>
      </Grid>
    </Grid>}
    <Dialog open={Boolean(pendingAction)} onClose={() => setPendingAction(null)} fullWidth maxWidth="xs">
      <DialogTitle>{ct('discardTitle')}</DialogTitle><DialogContent>{ct('discardBody')}</DialogContent>
      <DialogActions><Button onClick={() => setPendingAction(null)}>{ct('keepEditing')}</Button>
        <Button color="error" onClick={() => { runAction(pendingAction); setPendingAction(null); }}>{ct('discard')}</Button></DialogActions>
    </Dialog>
  </Stack>;
}

function CommunicationsWorkspace({ token, tenant, viewCommunications, viewTemplates, manageTemplates, manageRules, canSend }) {
  const { t } = useTranslation();
  const capabilities = useSpaCommunicationCapabilities(token, tenant, viewCommunications);
  const initial = viewCommunications ? 'history' : 'templates';
  const [tab, setTab] = useState(initial);
  const [visited, setVisited] = useState([initial]);
  const select = (_, value) => { setTab(value); setVisited((previous) => previous.includes(value) ? previous : [...previous, value]); };
  return <Stack spacing={3}>
    <Typography component="h1" variant="h5" fontWeight={800}>{t('spa.communications.pageTitle')}</Typography>
    {viewCommunications && <SpaCommunicationChannels state={capabilities} />}
    <Tabs value={tab} onChange={select} variant="scrollable" scrollButtons="auto" aria-label={t('spa.communications.pageTitle')}>
      {viewCommunications && <Tab value="history" label={t('spa.communications.historyTab')} id="communications-tab-history" aria-controls="communications-panel-history" />}
      {viewCommunications && <Tab value="rules" label={t('spa.communications.rulesTab')} id="communications-tab-rules" aria-controls="communications-panel-rules" />}
      {viewTemplates && <Tab value="templates" label={t('spa.communications.templatesTab')} id="communications-tab-templates" aria-controls="communications-panel-templates" />}
    </Tabs>
    {viewCommunications && visited.includes('history') && <Box role="tabpanel" id="communications-panel-history" aria-labelledby="communications-tab-history" hidden={tab !== 'history'}>
      <SpaCommunicationHistory token={token} tenant={tenant} capabilities={capabilities.data} canSend={canSend} />
    </Box>}
    {viewCommunications && visited.includes('rules') && <Box role="tabpanel" id="communications-panel-rules" aria-labelledby="communications-tab-rules" hidden={tab !== 'rules'}>
      <SpaCommunicationRules token={token} tenant={tenant} capabilities={capabilities.data} manage={manageRules} />
    </Box>}
    {viewTemplates && visited.includes('templates') && <Box role="tabpanel" id="communications-panel-templates" aria-labelledby="communications-tab-templates" hidden={tab !== 'templates'}>
      <TemplatesWorkspace token={token} tenant={tenant} manage={manageTemplates} capabilities={capabilities.data} />
    </Box>}
  </Stack>;
}

export default function SpaCommunicationsPage() {
  const { t } = useTranslation();
  const { can, loading } = usePermissions();
  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');
  const spa = !loading && localStorage.getItem('tenantType')?.toUpperCase() === 'SPA';
  const viewTemplates = spa && can('spa-settings:view');
  const viewCommunications = spa && can('spa-communications:view');
  const manageTemplates = viewTemplates && can('spa-settings:manage');
  const manageRules = viewCommunications && can('spa-communications:manage');
  const canSend = viewCommunications && can('spa-communications:send');
  return <SidebarLayout><Box sx={{ maxWidth: 1280, mx: 'auto', p: { xs: 2, md: 3 }, minWidth: 0 }}>
    {loading ? <CircularProgress size={24} /> : (viewTemplates || viewCommunications) && token && tenant
      ? <CommunicationsWorkspace key={`${tenant}:${token}:${viewTemplates}:${viewCommunications}:${manageTemplates}:${manageRules}:${canSend}`}
        token={token} tenant={tenant} viewTemplates={viewTemplates} viewCommunications={viewCommunications}
        manageTemplates={manageTemplates} manageRules={manageRules} canSend={canSend} />
      : <Alert severity="warning">{t('spa.communications.templatesDenied')}</Alert>}
  </Box></SidebarLayout>;
}
